import type { Amount } from './Amount';

type PayeeVerificationResultEnum =
    | 'MATCH'
    | 'CLOSE_MATCH'
    | 'NO_MATCH'
    | 'NOT_POSSIBLE'
    | 'NOT_SUPPORTED'
    | 'VERIFICATION_DISABLED'
    | 'NO_CANDIDATE';

type PayeeVerificationResult = {
    id?: string;
    result: PayeeVerificationResultEnum;
    verifiedParticipantName?: string;
};

type AggregatedPayeeVerificationResults = Array<{
    itemsCount?: number;
    result?: PayeeVerificationResultEnum;
}>;

type AggregatedConvertToStandardIfInstantFails = Array<{
    value: boolean;
    itemsCount: number;
}>;

export interface Phone {
    id: string;
    alias: string;
    number: string;
}

export interface Fee {
    fee: Amount;
    type?: string;
    descriptionI18N: string;
}

export enum EXCHANGE_RATE_TYPE {
    LIVE = 'LIVE',
    LIVE_WITH_DISCOUNT = 'LIVE_WITH_DISCOUNT',
    INFORMATIVE = 'INFORMATIVE',
    STANDARD = 'STANDARD',
    INDIVIDUAL = 'INDIVIDUAL',
}

export enum OPERATION_INFO_TYPE {
    WARNING = 'WARNING',
    INFO = 'INFO',
}

export enum SigningState {
    OPEN = 'OPEN',
    DONE = 'DONE',
    NONE = 'NONE',
    COSIGNING = 'COSIGNING',
    BLOCKED = 'BLOCKED',
    PROCESSING = 'PROCESSING',
}

interface OperationInfo {
    type: OPERATION_INFO_TYPE;
    customerMessageI18N: string;
}

export interface ExchangeRateInfo {
    rateType: EXCHANGE_RATE_TYPE;
    id?: string;
    fixedCurrency: string;
    fixedCurrencyRateUnit: number;
    variableCurrency: string;
    rate: number;
    validity?: number;
    dateOfRate?: string;
    middleRate?: number;
    conversionCost?: Amount;
}

export interface DiscountLiveRateLimit {
    type: 'MONTHLY' | 'CURRENT' | 'NEW';
    limit: Amount;
}

export interface MiddleRateInfoCZ {
    fixedCurrency: string;
    fixedCurrencyRateUnit: number;
    variableCurrency: string;
    rate: number;
}

export type Signable<T> = T & { signInfo: SignInfo };

type InterestRateInfo = {
    accruedInterest: Amount;
    rate: number;
    totalAmount: Amount;
    validity?: number;
};

export interface SignInfo {
    signId?: string;
    state?: SigningState;
    fees?: Fee[];
    exchangeRateInfo?: ExchangeRateInfo;
    equivalentAmount?: Amount;
    totalAmountWithFees?: Amount;
    operationInfo?: OperationInfo[];
    totalFees?: Amount;
    'cz-middleRateInfo'?: MiddleRateInfoCZ;
    'cz-conversionCost'?: Amount;
    discountLiveRateLimits?: DiscountLiveRateLimit[];
    equivalentAmountOutgoingByCurrency?: [Amount];
    equivalentAmountIncomingByCurrency?: [Amount];
    totalAmountOutgoingByCurrency?: [Amount];
    totalAmountIncomingByCurrency?: [Amount];
    outgoingAmountConversions?: [ExchangeRateInfo];
    incomingAmountConversions?: [ExchangeRateInfo];
    interestRateInfo?: InterestRateInfo;
    payeeVerificationResult?: PayeeVerificationResult;
    aggregatedPayeeVerificationResults?: AggregatedPayeeVerificationResults;
    aggregatedConvertToStandardIfInstantFails?: AggregatedConvertToStandardIfInstantFails;
}

export interface ErrorParameter {
    [key: string]: string;
}

export interface SigningError {
    error: string;
    message?: string;
    parameters?: ErrorParameter;
}

export enum AuthorizationType {
    TAC = 'TAC',
    TAC_PIN = 'TAC_PIN',
    SMS = 'SMS',
    CASE_MOBILE = 'CASE_MOBILE',
    CARDTAN = 'CARDTAN',
    SID = 'SID',
    NONE = 'NONE',
    E_DEVICE = 'E_DEVICE',
    L_DEVICE = 'L_DEVICE',
    INTEGRATED_TOKEN = 'INTEGRATED_TOKEN',
    TRUSTED = 'TRUSTED',
    GEORGE_TOKEN = 'GEORGE_TOKEN',
    IN_BRANCH_AUTHORIZATION = 'IN_BRANCH_AUTHORIZATION',
    HW_KEY = 'HW_KEY',
}

export interface SubmitSigning {
    authorizationType: AuthorizationType;
    oneTimePassword?: string;
    pinPassword?: string;
}

export enum GET_PAYLOAD_MESSAGES_FLAG {
    COLLECTIVE_SIGNING = 'collectiveSigning',
    REQUEST_AFTER_CUT_OFF_TIME = 'requestAfterCutOffTime',
    TRUSTED_SIGNING_TEMPORARY_SUSPENDED = 'trustedSigningTemporarySuspended',
    TRUSTED_SIGNING_NOT_APPLIED = 'trustedSigningNotApplied',
    TRUSTED_SIGNING_LIMIT_EXCEEDED = 'trustedSigningLimitExceeded',
    RO_REGULATION_4_2005 = 'ro-regulation-4-2005',
}

export enum GET_PAYLOAD_TRIGGER_FLAG {
    authorizationTypeTrigerred = 'authorizationTypeTrigerred',
    authorizationTypeTriggered = 'authorizationTypeTriggered',
}

export type SignGetPayloadFlag = GET_PAYLOAD_MESSAGES_FLAG | GET_PAYLOAD_TRIGGER_FLAG;

export interface SignScenario {
    authorizationType: AuthorizationType;
    phones?: Phone[];
}
export interface EbicsInfo {
    connectionId: string;
    userNumber: string;
    hostId: string;
}
export interface Disposer {
    id: string;
    username: string;
    authorizationType: AuthorizationType; // Pre selected authorization type
    scenarios: SignScenario[];
    ebicsInfo?: EbicsInfo[];
}
export interface SignGetPayload {
    authorizationType?: AuthorizationType;
    scenarios?: AuthorizationType[] | AuthorizationType[][];
    phones?: Phone[];
    flags?: SignGetPayloadFlag[];
    signInfo?: SignInfo;
    disposers?: Disposer[];
}

export interface PollResponse {
    state: PollingState;
    pollInterval: number;
}

export enum PollingState {
    PROCESSING = 'PROCESSING',
    AUTHORIZED = 'AUTHORIZED',
    DONE = 'DONE',
    FALLBACK = 'FALLBACK',
    REJECTED = 'REJECTED',
    EXPIRED = 'EXPIRED',
    NOT_SIGNABLE = 'NOT_SIGNABLE',
    SIGNING_FAILED = 'SIGNING_FAILED',
    BLOCKED = 'BLOCKED',
    BLOCKED_INVESTIGATION = 'BLOCKED_INVESTIGATION',
    BLOCKED_FRAUD = 'BLOCKED_FRAUD',
    BLOCKED_SENDER_FRAUD_RISK = 'BLOCKED_SENDER_FRAUD_RISK',
    ORDER_PROCESSING_FAILED = 'ORDER_PROCESSING_FAILED',
    AUTHORIZATION_NOT_STARTED = 'AUTHORIZATION_NOT_STARTED',
    AUTHORIZED_BUT_PROCESSING_ERRORS = 'AUTHORIZED_BUT_PROCESSING_ERRORS',
    SUSPICIOUS_ACTIVITY = 'SUSPICIOUS_ACTIVITY',
}
